import {AxiosError, isAxiosError} from 'axios';
import dayjs from 'dayjs';
import {LitElement, html, css, unsafeCSS} from 'lit';
import {customElement, property, query, state} from 'lit/decorators.js';
import _debounce from 'lodash/debounce';
import mapboxgl, {LngLatLike, Map as MapboxMap} from 'mapbox-gl';
import mapboxCSS from 'mapbox-gl/dist/mapbox-gl.css?inline';
import {makeStaticMapUrl} from './api/static-mapbox';
import {getTrip, getTrips, setApiToken} from './api/trips';
import {TripData} from './api/trips-api';
import {renderInteractive, renderStatic, resetMappedTrips} from './map/trips';
import {
  defaultGeocodingHandler,
  setReverseGeocodingEnabled,
  setGeocodingHandler,
  setMapboxAccessToken,
} from './services/geocode';
import appCss from './styles/app.css?inline';
import tripMarkerCss from './styles/trip-marker.css?inline';
import tripPopupCss from './styles/trip-popup.css?inline';

import './zubie-spinner';
import './zubie-tooltip';

export const CENTER_OF_US = [-98.5795, 39.828175] as LngLatLike;

export const DEFAULT_MAP_STYLE = 'mapbox://styles/mapbox/standard';

@customElement('zubie-trips-map')
export class ZubieTripsMap extends LitElement {
  static override styles = css`
    ${unsafeCSS(appCss)}
    ${unsafeCSS(tripMarkerCss)}
    ${unsafeCSS(tripPopupCss)}
  `;

  @property({
    type: Array,
    attribute: 'default-center',
    converter: {
      fromAttribute(value: string): number[] {
        return value.split(',').map((coord) => parseFloat(coord.trim()));
      },
      toAttribute(value: number[]): string {
        return value.join(',');
      },
    },
  })
  defaultCenter?: LngLatLike = CENTER_OF_US;

  @property({type: Number, attribute: 'default-zoom'})
  defaultZoom?: number = 3;

  @property({type: String, attribute: 'mapbox-access-token'})
  mapboxAccessToken?: string;

  @property({type: String, attribute: 'map-style'})
  mapStyle?: string;

  @property({type: String, attribute: 'map-type'})
  mapType?: 'interactive' | 'static' = 'interactive';

  @property({type: Boolean, attribute: 'reverse-geocode'})
  reverseGeocode? = false;

  @property({type: String, attribute: 'zubie-token'})
  zubieToken?: string;

  @property({attribute: false})
  reverseGeocodeHandler?: typeof defaultGeocodingHandler;

  @property({
    type: String,
    attribute: 'started-after',
    converter: {
      fromAttribute(value: string): string {
        if (!value) {
          return '';
        }
        return dayjs(value).format();
      },
      toAttribute(value: string): string {
        return dayjs(value).toISOString();
      },
    },
  })
  startedAfter?: string;

  @property({
    type: String,
    attribute: 'started-before',
    converter: {
      fromAttribute(value: string): string {
        if (!value) {
          return '';
        }
        return dayjs(value).format();
      },
      toAttribute(value: string): string {
        return dayjs(value).toISOString();
      },
    },
  })
  startedBefore?: string;

  @property({type: String, attribute: 'trip-key'})
  tripKey?: string;

  @property({type: String, attribute: 'vehicle-key'})
  vehicleKey?: string;

  @state()
  private mapLoading = false;

  /**
   * Static Map URL
   */
  @state()
  private staticMapUrl = '';

  @query('.interactive-map')
  private mapContainer!: HTMLDivElement;

  /**
   * Debounced version of the updateTrips() method
   */
  private debouncedUpdateTrips: ReturnType<typeof _debounce>;

  /**
   * Default "image" to be used for static map before one is generated (first load).
   */
  private defaultStaticMap?: string;

  /**
   * Flag indicating that map was rendered.
   */
  private didRender = false;

  /**
   * The trip/vehicle key + date range combo that was last fetched
   */
  private lastFetchedQuery?: string;

  /**
   * The Mapbox map instance.
   */
  private map?: MapboxMap;

  /**
   * Flag to indicate if the map has been loaded.
   */
  private mapLoaded = false;

  /**
   * The last style applied to the interactive map.
   */
  private mapStyleInUse?: string;

  /**
   * The trips data to be displayed on the map.
   */
  private trips: TripData[] = [];

  constructor() {
    super();
    this.debouncedUpdateTrips = _debounce(() => this.updateTrips(), 100);
  }

  override firstUpdated() {
    // Inject Mapbox CSS into the shadow root
    const styleTag = document.createElement('style');
    styleTag.textContent = mapboxCSS;
    this.shadowRoot?.appendChild(styleTag);

    this.debouncedUpdateTrips();
    this.initializeMaps();
    this.updateHandlers();

    this.dispatch('mount', this);
  }

  override updated(changedProps: Map<string, string>) {
    if (
      [...changedProps.keys()].some((value) =>
        [
          'mapStyle',
          'mapType',
          'mapboxAccessToken',
          'startedAfter',
          'startedBefore',
          'tripKey',
          'vehicleKey',
        ].includes(value)
      )
    ) {
      /**
       * These reactive properties should trigger
       * a trips update or a map initialization
       */

      if (
        changedProps.has('mapboxAccessToken') ||
        changedProps.has('mapStyle') ||
        changedProps.has('mapType') ||
        (!this.vehicleKey && !this.tripKey)
      ) {
        this.didRender = false; // need to re-render without requesting data update
        this.initializeMaps();
      }

      this.debouncedUpdateTrips();
    }

    if (changedProps.has('mapboxAccessToken') && this.mapboxAccessToken) {
      // Mapbox access token changed
      setMapboxAccessToken(this.mapboxAccessToken);
    }

    if (changedProps.has('zubieToken') && this.zubieToken) {
      // Zauth token changed
      setApiToken(this.zubieToken);
      if (!this.didRender) {
        // Likely still need to render, if token changed after API call failed
        this.debouncedUpdateTrips();
      }
    }

    if (changedProps.has('reverseGeocodeHandler')) {
      // Reverse geocode handler changed
      this.updateHandlers();
    }

    if (changedProps.has('reverseGeocode')) {
      // Reverse geocode property changed
      setReverseGeocodingEnabled(Boolean(this.reverseGeocode));
    }
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.dispatch('unmount', this);
  }

  dispatch(eventName: string, detail: unknown) {
    this.dispatchEvent(
      new CustomEvent(eventName, {
        bubbles: true,
        composed: true,
        detail,
      })
    );
  }

  async getTrip() {
    if (!this.tripKey || !this.vehicleKey) {
      return;
    }

    const query = `trip_${this.tripKey}`;

    if (query !== this.lastFetchedQuery) {
      // Trip key changed

      this.mapLoading = true;
      this.didRender = false;
      this.dispatch('trip-loading', true);

      try {
        const trip = await getTrip(this.tripKey, this.vehicleKey);
        this.trips = [trip];
        this.renderWhenReady();
      } catch (error) {
        this.handleApiError(<Error | AxiosError>error);
        this.trips = [];
        this.lastFetchedQuery = ''; // still need the data
      }

      this.dispatch('trip-loading', false);
    } else if (!this.didRender) {
      // Trip key is the same, but we need to render
      this.renderWhenReady();
    }

    this.lastFetchedQuery = query;
  }

  async getVehicleTrips() {
    if (!this.vehicleKey) {
      return;
    }

    if (!this.didRender) {
      this.mapLoading = true;
    }

    const query = `vehicle_${this.vehicleKey}_${this.startedAfter || ''}_${
      this.startedBefore || ''
    }`;

    if (query === this.lastFetchedQuery) {
      // We already have the data, don't fetch anything

      if (!this.didRender) {
        // We have the data, but need to render
        this.renderWhenReady();
      }
      return;
    }

    this.lastFetchedQuery = query;

    const options = {} as Parameters<typeof getTrips>[1];

    if (!this.startedAfter && !this.startedBefore) {
      options.size = 1;
    } else {
      // We know that one of these is specified
      options.startedAfter = this.startedAfter;
      options.startedBefore = this.startedBefore;

      if (this.startedAfter && !this.startedBefore) {
        // No end date/time, use end-of-day
        options.startedBefore = dayjs(this.startedAfter).endOf('day').format();
      } else if (this.startedBefore && !this.startedAfter) {
        // No start date/time, use start-of-day
        options.startedAfter = dayjs(this.startedBefore)
          .startOf('day')
          .format();
      } else if (!dayjs(this.startedBefore).isSame(this.startedAfter, 'day')) {
        // Has both dates/times specified, but end date/time goes beyond a day, use to end-of-day
        options.startedBefore = dayjs(this.startedAfter).endOf('day').format();
      }
    }

    this.didRender = false;
    this.dispatch('trip-loading', true);

    try {
      const trips = await getTrips(this.vehicleKey, options);
      this.trips = trips;
      this.renderWhenReady();
    } catch (error) {
      this.handleApiError(<Error | AxiosError>error);
      this.trips = [];
      this.lastFetchedQuery = ''; // still need the data
    }

    this.dispatch('trip-loading', false);
  }

  handleApiError(error: Error) {
    if (isAxiosError(error) && error.status === 401) {
      this.dispatch('auth-error', error);
    } else {
      this.dispatch('api-error', error);
    }
    this.dispatch('error', error);
  }

  initializeMaps() {
    if (this.mapType === 'static') {
      this.defaultStaticMap = makeStaticMapUrl({
        center: this.defaultCenter as [number, number],
        padding: 0,
        height: this.clientHeight,
        width: this.clientWidth,
        style: this.mapStyle,
        token: <string>this.mapboxAccessToken,
        zoom: this.defaultZoom,
      });
      if (!this.vehicleKey && !this.tripKey) {
        this.staticMapUrl = this.defaultStaticMap;
      }
    } else {
      const mapStyle = this.mapStyle || DEFAULT_MAP_STYLE;

      if (!this.map) {
        this.mapStyleInUse = mapStyle;
        this.map = new mapboxgl.Map({
          accessToken: this.mapboxAccessToken,
          container: this.mapContainer,
          center: this.defaultCenter,
          zoom: this.defaultZoom,
          style: mapStyle,
        })
          .on('error', (error) => {
            this.dispatch('error', error);
          })
          .on('load', () => {
            this.mapLoaded = true;
            this.dispatch('map-load', this.map);
            this.renderWhenReady();
          });
      } else {
        // Map already exists
        if (!this.vehicleKey && !this.tripKey) {
          this.trips = [];
          this.map.setCenter(this.defaultCenter);
          this.map.setZoom(this.defaultZoom);
        }

        if (mapStyle !== this.mapStyleInUse) {
          this.mapStyleInUse = mapStyle;

          // Wait for style to load
          const waitForLoad = () => {
            try {
              this.map?.getStyle();
            } catch (error) {
              if ((<Error>error).message === 'Style is not done loading') {
                return;
              }
            }

            this.map?.off('styledata', waitForLoad);
            this.renderWhenReady();
          };

          this.map.off('styledata', waitForLoad); // in case we already added it
          this.map.on('styledata', waitForLoad);
          this.map.setStyle(mapStyle);
        } else {
          this.renderWhenReady();
        }
      }
    }
  }

  async renderWhenReady() {
    if (this.didRender) {
      return;
    }

    if (this.mapType === 'static') {
      this.didRender = true;

      if (!this.trips.length) {
        this.staticMapUrl = <string>this.defaultStaticMap;
      } else {
        this.staticMapUrl = await renderStatic({
          trips: this.trips,
          container: this,
          style: this.mapStyle,
          token: <string>this.mapboxAccessToken,
        });
      }

      this.dispatch('trip-render', this.map);
      this.mapLoading = false;
      return;
    }

    if (this.mapLoaded) {
      this.didRender = true;
      const success = await renderInteractive({
        trips: this.trips,
        map: this.map,
        reverseGeocode: this.reverseGeocode,
      });

      if (!success) {
        this.didRender = false;
      } else {
        this.dispatch('trip-render', this.map);
        this.mapLoading = false;
      }
    }
  }

  updateHandlers() {
    if (this.reverseGeocodeHandler) {
      setGeocodingHandler(this.reverseGeocodeHandler);
    }
  }

  updateTrips() {
    if (
      (!this.tripKey && this.lastFetchedQuery?.startsWith('trip_')) ||
      (!this.vehicleKey && this.lastFetchedQuery?.startsWith('vehicle_'))
    ) {
      // Trip key/vehicle key removed, but we fetched trips from it
      this.trips = []; // clear out trips
      this.lastFetchedQuery = '';
      resetMappedTrips(this.map);
    }

    if (this.tripKey) {
      this.getTrip();
    } else if (this.vehicleKey) {
      this.getVehicleTrips();
    }
  }

  override render() {
    return html`
      <div class="zubie-map ${this.mapType} row justify-center items-center">
        <div class="interactive-map"></div>
        ${this.mapLoading
          ? html`
              <div class="loading-backdrop row justify-center items-center">
                <zubie-spinner
                  class="loading-spinner"
                  height="50px"
                  width="50px"
                ></zubie-spinner>
              </div>
            `
          : ''}
        ${this.staticMapUrl ? html` <img src="${this.staticMapUrl}" /> ` : ''}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'zubie-trips-map': ZubieTripsMap;
  }
}
